import { useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";
import { animeList } from "@/mocks/anime";
import { categories, siteInfo } from "@/mocks/site";
import type { AnimeWork } from "@/types";

export default function Ranking() {
  const [active, setActive] = useState<string>("all");

  const list = (
    active === "all" ? animeList : animeList.filter((item) => item.category === active)
  ) as AnimeWork[];

  const activeCategory = categories.find((cat) => cat.id === active);

  useSeo({
    title: "编辑精选榜 - 按主题挑动漫作品 | 樱花动漫社",
    description:
      "樱花动漫社编辑精选榜，按热血冒险、青春治愈、科幻机战、恋爱日常、悬疑奇幻五个方向整理推荐作品，每一条都附上推荐理由，帮你快速决定先看哪一部。",
    keywords: "动漫推荐榜,动漫排行,编辑精选,动漫榜单,动漫推荐",
    path: "/ranking",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "编辑精选榜",
        url: `${siteInfo.domain}/ranking`,
        description: "按主题方向整理的动漫作品推荐榜单。",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: siteInfo.domain },
          { "@type": "ListItem", position: 2, name: "编辑精选榜", item: `${siteInfo.domain}/ranking` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: activeCategory ? `${activeCategory.name}推荐` : "全部推荐",
        numberOfItems: list.length,
        itemListElement: list.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.title,
          url: `${siteInfo.domain}/works/${item.id}`,
        })),
      },
    ],
  });

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 md:px-6 md:py-14">
      <Breadcrumb items={[{ label: "编辑精选榜" }]} />

      <div className="mt-6 max-w-3xl">
        <h1 className="font-heading text-3xl font-700 tracking-tight text-foreground-950 md:text-4xl">
          编辑精选榜
        </h1>
        <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
          这份榜单不追求绝对的先后，而是按“你想被哪种情绪陪伴”来整理。选一个方向，
          看我们为什么把它放进推荐里。
        </p>
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setActive("all")}
          className={`rounded-full px-4 py-1.5 text-sm transition-colors ${
            active === "all"
              ? "bg-primary-500 text-background-50"
              : "bg-background-100 text-foreground-700 hover:bg-background-200"
          }`}
        >
          全部方向
        </button>
        {categories.map((cat) => (
          <button
            key={cat.id}
            type="button"
            onClick={() => setActive(cat.id)}
            className={`rounded-full px-4 py-1.5 text-sm transition-colors ${
              active === cat.id
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-background-200"
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {activeCategory && (
        <p className="mt-5 rounded-xl bg-accent-50 p-4 text-sm text-foreground-700">
          {activeCategory.desc}
        </p>
      )}

      <ol className="mt-8 space-y-4">
        {list.map((work, index) => {
          const category = categories.find((cat) => cat.id === work.category);
          return (
            <li key={work.id}>
              <Link
                to={`/works/${work.id}`}
                className="group flex flex-col gap-5 rounded-xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 sm:flex-row sm:items-center md:p-5"
              >
                <div className="flex items-center gap-4">
                  <span className="font-heading text-3xl font-900 text-background-300 transition-colors group-hover:text-primary-300 sm:text-4xl">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <img
                    src={work.cover}
                    alt={`《${work.title}》封面插画`}
                    width={600}
                    height={800}
                    className="h-[100px] w-[75px] shrink-0 rounded-lg object-cover object-top"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-0.5 text-primary-800">
                      <i className={category?.icon || "ri-bookmark-line"} aria-hidden="true"></i>
                      {category?.name}
                    </span>
                    <span className="text-foreground-500">
                      {work.year} · {work.episodes} 集 · {work.studio}
                    </span>
                  </div>
                  <h2 className="mt-2 font-heading text-lg font-700 text-foreground-950 transition-colors group-hover:text-primary-700">
                    {work.title}
                  </h2>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{work.summary}</p>
                  <p className="mt-2 flex items-start gap-1.5 text-xs text-secondary-700">
                    <i className="ri-lightbulb-flash-line mt-0.5" aria-hidden="true"></i>
                    看点：{work.highlights[0]}
                  </p>
                </div>
                <span className="hidden shrink-0 items-center gap-1 whitespace-nowrap text-sm font-500 text-primary-700 sm:flex">
                  看详情
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </span>
              </Link>
            </li>
          );
        })}
      </ol>

      <div className="mt-12 flex flex-col items-center rounded-2xl bg-secondary-50 p-8 text-center">
        <h2 className="font-heading text-xl font-700 text-foreground-950 md:text-2xl">
          想自己慢慢逛作品库？
        </h2>
        <p className="mt-2 max-w-lg text-sm leading-relaxed text-foreground-600">
          榜单只是入口。作品库里可以按分类、标签和关键词自由筛选，找到更符合你口味的那一部。
        </p>
        <Link
          to="/works"
          className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-500 text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-stack-line" aria-hidden="true"></i>
          打开作品库
        </Link>
      </div>
    </div>
  );
}